import { createAction } from '@reduxjs/toolkit';

import { fetchCabins } from '../../services/cruise';
import { fetchLabels } from '../../services/content';
import { urlUtils } from '../../utils/url';
import { getProductsIncluded } from '../../utils/mappers/productsIncluded';
import { logSearch, logEcom } from '../../utils/logUtils/CabinListLogUtils';

// Every action the thunk dispatches lives here rather than on a slice, so the actions module and
// the slices never import each other. `cabinSelected` is the one action no thunk dispatches, so
// it is slice-generated on `cruise`.
export const initSucceeded = createAction('cabinList/initSucceeded');
export const initFailed = createAction('cabinList/initFailed');
export const initFinished = createAction('cabinList/initFinished');
export const messageAdded = createAction('cabinList/messageAdded');

const GENERIC_ERROR = {
  title: 'An error occurred, please redo your search.',
  severity: 'ERROR',
};

const getLabels = async (siteSettings) => {
  const labels = await fetchLabels(siteSettings, 'iwCabinList');
  return labels;
};

const getCabins = async (siteSettings, userId) => {
  const result = await fetchCabins(siteSettings, userId);
  return result;
};

// Stays a hand-written thunk, per the D1 refinement made in the transfer-list conversion: the flow
// has three terminal outcomes — cabins found, no cabins, or a thrown fetch — and `initFinished`
// fires on all three, so they do not map onto one fulfilled/rejected pair.
//
// The dispatch order differs between branches in the original and is preserved exactly: on the
// first two branches `initFinished` goes **last**, while in the catch it goes **first**, before the
// generic error message. The old four-level nesting (`init` -> `initFinished` -> `initSuccess`) is
// flattened; the session-id URL write still happens immediately before `initSucceeded`, as it did
// when it ran at `initSuccess` call time rather than inside the returned thunk.
export const init = () => async (dispatch, getState) => {
  const { siteSettings, userId } = getState().common;

  try {
    const [labels, data] = await Promise.all([getLabels(siteSettings), getCabins(siteSettings, userId)]);

    if (data.statusCode !== 200) {
      dispatch(
        messageAdded({
          title: labels ? labels.iwErrors?.systemError?.heading : GENERIC_ERROR.title,
          severity: 'ERROR',
        }),
      );
    }

    const { selectedPackage, loggingData } = data?.result ?? {};

    if (selectedPackage?.cruise?.cabins?.length > 0) {
      urlUtils.updateUrlWithSessionId(data.result.serviceCallInformation.sessionId);
      const { general, ...restLabels } = labels;

      dispatch(
        initSucceeded({
          labels: {
            ...restLabels,
            ...general,
            productsIncludedParsed: getProductsIncluded(selectedPackage.packageTag, general),
          },
          cruise: selectedPackage.cruise,
          hotels: selectedPackage.hotels,
          transport: selectedPackage.transport,
          totalDuration: selectedPackage.totalDuration,
          numberOfHotelNights: selectedPackage.numberOfHotelNights,
          searchInformation: data.result.searchInformation,
          priceDetails: selectedPackage.priceBreakDown,
        }),
      );
    } else {
      dispatch(initFailed({ labels }));
    }

    if (data?.translatedMessage) {
      dispatch(messageAdded(data.translatedMessage));
    }

    if (loggingData) {
      logSearch(loggingData, selectedPackage?.cruise);
      if (!urlUtils.isChangeCabinPage()) {
        logEcom(selectedPackage);
      }
    }

    dispatch(initFinished());
  } catch (_error) {
    dispatch(initFinished());
    dispatch(messageAdded(GENERIC_ERROR));
  }
};
